import React from 'react';
import PropTypes from 'prop-types';

import {Box, Pill, Stack, Typography} from 'views/components';
import {Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator} from 'views/components';
import {translate} from 'views/module';

import {MANUAL_COMPLETION_KIND, TONE_PILL_PROPS, humanizeKind} from './workflow_status';
import {formatDateTime, translateOr} from '../format';

import backend from 'utils/backend';
import userUtil from 'utils/miscellaneous/user/user_util';

const DELIVERY_TONES = {
  PENDING: 'neutral',
  SENDING: 'neutral',
  SENT: 'info',
  SUPPRESSED: 'neutral',
  FAILED: 'error',
};

const CompletedBy = (props) => {
  const {userRn} = props;
  const [name, setName] = React.useState(null);

  React.useEffect(() => {
    let isCancelled = false;
    setName(null);

    const loadName = async () => {
      try {
        const result = await backend.user.getUser(userRn, false);
        const fullName = userUtil.getFullName(result?.user?.firstName, result?.user?.lastName);
        if (!isCancelled && fullName) setName(fullName);
      } catch (loadError) {
        console.error(loadError);
      }
    };

    loadName();

    return () => {
      isCancelled = true;
    };
  }, [userRn]);

  return (
    <Typography sx={{fontSize: '11px', color: '#A8A49E'}}>
      {translate('loomLead.LoomLeadDetail.Workflows.Timeline.CompletedBy', {value: name || userRn})}
    </Typography>
  );
};
CompletedBy.propTypes = {
  userRn: PropTypes.string.isRequired,
};

const SignalEntry = (props) => {
  const {entry} = props;

  return (
    <Stack spacing={0.5}>
      <Typography sx={{fontSize: '13px', fontWeight: 500, color: '#1A1916'}}>
        {translateOr(`loomLead.LoomLeadDetail.Workflows.Timeline.Signal.${entry.kind}`, humanizeKind(entry.kind))}
      </Typography>
      {
        entry.summary &&
        <Typography sx={{fontSize: '13px', color: '#6B6760', whiteSpace: 'pre-wrap'}}>
          {entry.summary}
        </Typography>
      }
      {
        entry.kind === MANUAL_COMPLETION_KIND && entry.sender &&
        <CompletedBy userRn={entry.sender} />
      }
      {
        entry.handledAt &&
        <Typography sx={{fontSize: '11px', color: '#A8A49E'}}>
          {translate('loomLead.LoomLeadDetail.Workflows.Timeline.Handled', {value: formatDateTime(entry.handledAt)})}
        </Typography>
      }
    </Stack>
  );
};
SignalEntry.propTypes = {
  entry: PropTypes.object.isRequired,
};

const EscalationEntry = (props) => {
  const {entry} = props;
  const deliveryTone = DELIVERY_TONES[entry.deliveryStatus] || 'neutral';

  return (
    <Stack spacing={0.5}>
      <Stack direction='row' alignItems='center' sx={{gap: '8px', flexWrap: 'wrap'}}>
        <Typography sx={{fontSize: '13px', fontWeight: 500, color: '#1A1916'}}>
          {translate('loomLead.LoomLeadDetail.Workflows.Timeline.Escalation')}
        </Typography>
        <Pill
          label={translateOr(`loomLead.LoomLeadDetail.Workflows.Timeline.Delivery.${entry.deliveryStatus}`, entry.deliveryStatus)}
          {...TONE_PILL_PROPS[deliveryTone]}
        />
        {
          entry.decision &&
          <Pill
            label={translate('loomLead.LoomLeadDetail.Workflows.Timeline.Decided', {
              decision: translateOr(`loomLead.LoomLeadDetail.Workflows.Timeline.Decision.${entry.decision}`, entry.decision),
              value: formatDateTime(entry.decidedAt),
            })}
            variant='neutral'
          />
        }
      </Stack>
      <Typography sx={{fontSize: '13px', color: '#6B6760', whiteSpace: 'pre-wrap'}}>
        {entry.reason}
      </Typography>
      {
        entry.summary &&
        <Typography sx={{fontSize: '13px', color: '#6B6760', whiteSpace: 'pre-wrap'}}>
          {entry.summary}
        </Typography>
      }
      {
        entry.message &&
        <Box
          sx={{
            mt: 0.5,
            p: 1.5,
            borderRadius: '8px',
            backgroundColor: '#F9F8F6',
            border: '1px solid #E8E4DE',
          }}
        >
          <Typography sx={{fontSize: '11px', color: '#A8A49E', mb: 0.5}}>
            {translate('loomLead.LoomLeadDetail.Workflows.Timeline.Draft')}
          </Typography>
          <Typography sx={{fontSize: '13px', color: '#1A1916', whiteSpace: 'pre-wrap'}}>
            {entry.message}
          </Typography>
        </Box>
      }
    </Stack>
  );
};
EscalationEntry.propTypes = {
  entry: PropTypes.object.isRequired,
};

const WorkflowTimeline = (props) => {
  const {timeline} = props;

  return (
    <Timeline
      sx={{
        'm': 0,
        'p': 0,
        '& .MuiTimelineItem-root:before': {
          flex: 0,
          p: 0,
        },
      }}
    >
      {
        timeline.map((entry, index) => {
          const isEscalation = entry.type === 'ESCALATION';

          return (
            <TimelineItem key={entry.key}>
              <TimelineSeparator>
                <TimelineDot
                  sx={{
                    boxShadow: 'none',
                    backgroundColor: isEscalation ? '#D97706' : '#DA7955',
                  }}
                />
                {
                  index < timeline.length - 1 &&
                  <TimelineConnector sx={{backgroundColor: '#E8E4DE'}} />
                }
              </TimelineSeparator>
              <TimelineContent sx={{pb: 2}}>
                <Typography sx={{fontSize: '11px', color: '#A8A49E', mb: 0.5}}>
                  {formatDateTime(entry.occurredAt)}
                </Typography>
                {
                  isEscalation ?
                    <EscalationEntry entry={entry} /> :
                    <SignalEntry entry={entry} />
                }
              </TimelineContent>
            </TimelineItem>
          );
        })
      }
    </Timeline>
  );
};
WorkflowTimeline.propTypes = {
  timeline: PropTypes.array.isRequired,
};

export default React.memo(WorkflowTimeline);
